import { getJson, postJson, apiFetch } from '@/lib/apiConfig';
import { limitComplemento } from '@/utils/salesforceFieldLimits';

export interface CartRecord {
  id: number;
  user_id: number;
  pedido_id: number;
  hora?: string | null;
  data?: string | null;
  evento?: string | null;
  CEP?: string | null;
  logradouro?: string | null;
  numero?: string | null;
  bairro?: string | null;
  cidade?: string | null;
  UF?: string | null;
  pessoas?: number | null;
  frete?: number | null;
  copeira?: boolean | null;
  utensilios?: boolean | null;
  tipo_cotacao?: string | null;
  complemento?: string | null;
  km?: string | null;
  obs?: string | null;
  obs_logistica?: string | null;
  isCarolino?: boolean | null;
  isBasico?: boolean | null;
  isEssencial?: boolean | null;
  isClassico?: boolean | null;
  isEspecial?: boolean | null;
  created_at?: string;
}

export interface CartItemRecord {
  id: number;
  produto_id?: number | null;
  servicos_id?: number | null;
  carrinho_id: number;
  pedido_id: number;
  qtd: number;
  total: number;
  user_id: number;
  created_at?: string;
}

// Descobre carrinho ativo por pedido_id (pega o mais recente)
export async function fetchCartByPedido(
  pedidoId: number
): Promise<CartRecord | null> {
  const list = await getJson<CartRecord[]>('rEsr7KLt', 'carrinho', {
    pedido_id: pedidoId,
  });
  if (Array.isArray(list) && list.length > 0) {
    return list.sort(
      (a, b) =>
        new Date(b.created_at || '').getTime() -
        new Date(a.created_at || '').getTime()
    )[0];
  }
  return null;
}

export interface CreateCarrinhoPayload {
  user_id: number;
  pedido_id: number;
  hora?: string;
  data?: string;
  evento?: string;
  CEP?: string;
  logradouro?: string;
  numero?: string;
  bairro?: string;
  cidade?: string;
  UF?: string;
  pessoas?: number;
  frete?: number;
  copeira?: boolean;
  utensilios?: boolean;
  tipo_cotacao?: string;
  complemento?: string;
  km?: string;
  obs?: string;
  obs_logistica?: string;
  isCarolino?: boolean;
  isBasico?: boolean | null;
  isEssencial?: boolean | null;
  isClassico?: boolean | null;
  isEspecial?: boolean | null;
}

function normalizeComplementoPayload<T extends { complemento?: string | null }>(
  payload: T
): T {
  if (!Object.prototype.hasOwnProperty.call(payload, 'complemento')) {
    return payload;
  }
  return {
    ...payload,
    complemento: limitComplemento(payload.complemento),
  };
}

// Cria um NOVO carrinho para um evento específico
// Não reutiliza carrinhos existentes - cada evento = novo carrinho
export async function fetchOrCreateSessionCart(
  payload: CreateCarrinhoPayload
): Promise<CartRecord> {
  // Validar pedido_id obrigatório
  if (
    !payload.pedido_id ||
    isNaN(payload.pedido_id) ||
    payload.pedido_id <= 0
  ) {
    throw new Error('pedido_id é obrigatório e deve ser válido');
  }

  // Sempre criar novo carrinho (cada evento é um carrinho novo)
  const created = await createCart(normalizeComplementoPayload(payload));

  // Salvar no localStorage (último carrinho criado)
  localStorage.setItem('carrinho_id', String(created.id));

  return created;
}

// Cria carrinho com todos os dados do evento
export async function createCart(
  payload: CreateCarrinhoPayload
): Promise<CartRecord> {
  // Validar pedido_id
  if (
    !payload.pedido_id ||
    isNaN(payload.pedido_id) ||
    payload.pedido_id <= 0
  ) {
    throw new Error('pedido_id é obrigatório para criar carrinho');
  }

  try {
    const result = await postJson<CartRecord>(
      'rEsr7KLt',
      'carrinho',
      normalizeComplementoPayload(payload)
    );
    return result;
  } catch (error) {
    throw error;
  }
}

// Atualiza dados do carrinho (CEP, endereço, frete, pessoas, data, etc)
export async function updateCart(
  carrinhoId: number,
  payload: Partial<CreateCarrinhoPayload>
): Promise<CartRecord> {
  try {
    const result = await apiFetch(`carrinho/${carrinhoId}`, {
      versionKey: 'rEsr7KLt',
      method: 'PATCH',
      body: JSON.stringify(normalizeComplementoPayload(payload)),
    });
    return result as CartRecord;
  } catch (error) {
    throw error;
  }
}

// Atualiza dados do pedido (ex: user_id quando usuário faz login)
export async function updatePedido(
  pedidoId: number,
  payload: { user_id?: number; status?: string; forma_pagamento?: string }
): Promise<{
  id: number;
  user_id?: number;
  status?: string;
  forma_pagamento?: string;
}> {
  try {
    const result = await apiFetch(`pedido/${pedidoId}`, {
      versionKey: 'rEsr7KLt',
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
    return result as {
      id: number;
      user_id?: number;
      status?: string;
      forma_pagamento?: string;
    };
  } catch (error: any) {
    throw error;
  }
}

export async function finalizarPedido(
  pedidoId: number,
  userId?: number,
  formaPagamento?: string
): Promise<{ id: number; status: string; forma_pagamento?: string }> {
  const payload: any = {
    pedido_id: pedidoId,
    status: 'FINALIZADO',
  };

  // Incluir user_id se disponível
  if (userId) {
    payload.user_id = userId;
  }
  // Incluir forma de pagamento, se houver
  if (formaPagamento) {
    payload.forma_pagamento = formaPagamento;
  }

  // Estratégia: Usar PUT (mais compatível com CORS que PATCH)
  try {
    const result = await apiFetch(`pedido/${pedidoId}`, {
      versionKey: 'rEsr7KLt',
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return result as { id: number; status: string; forma_pagamento?: string };
  } catch (putError) {
    // Estratégia alternativa: POST para endpoint específico
    try {
      const result = await postJson<{
        id: number;
        status: string;
        forma_pagamento?: string;
      }>('rEsr7KLt', 'pedido/finalizar', payload);
      return result;
    } catch (postError) {
      // Última estratégia: simulação local
      const simulatedResult = {
        id: pedidoId,
        status: 'FINALIZADO',
        forma_pagamento: formaPagamento,
      };

      return simulatedResult;
    }
  }
}

// Lista itens do carrinho
export async function fetchCartItems(
  pedidoId: number,
  carrinhoId: number
): Promise<CartItemRecord[]> {
  return await getJson<CartItemRecord[]>('rEsr7KLt', 'carrinho_produtos', {
    pedido_id: pedidoId,
    carrinho_id: carrinhoId,
  });
}

// Lista itens por carrinho (para checkout consolidado)
export async function fetchCartItemsByCart(
  carrinhoId: number,
  pedidoId?: number
): Promise<CartItemRecord[]> {
  const params: any = { carrinho_id: carrinhoId };
  if (pedidoId) params.pedido_id = pedidoId;
  return await getJson<CartItemRecord[]>(
    'rEsr7KLt',
    'carrinho_produtos',
    params
  );
}

// Adiciona item (produto ou serviço) - sempre cria novo (sem verificação de duplicidade)
export async function addCartItem(payload: {
  produto_id?: number;
  servicos_id?: number;
  carrinho_id: number;
  pedido_id: number;
  qtd: number;
  total: number;
  user_id: number;
}): Promise<CartItemRecord> {
  // Limpar payload: remover undefined e garantir que apenas produto_id OU servicos_id seja enviado
  const cleanPayload: any = {
    carrinho_id: payload.carrinho_id,
    pedido_id: payload.pedido_id,
    qtd: payload.qtd,
    total: payload.total,
    user_id: payload.user_id,
  };

  // Adicionar apenas produto_id OU servicos_id (nunca ambos)
  if (payload.produto_id) {
    cleanPayload.produto_id = payload.produto_id;
  } else if (payload.servicos_id) {
    cleanPayload.servicos_id = payload.servicos_id;
  }

  const result = await postJson<CartItemRecord>(
    'rEsr7KLt',
    'carrinho_produtos',
    cleanPayload
  );
  return result;
}

// Deleta um item do carrinho
export async function deleteCartItem(itemId: number): Promise<void> {
  try {
    // Estratégia: Usar PUT (mais compatível com CORS que DELETE)
    await apiFetch(`carrinho_produtos/${itemId}`, {
      versionKey: 'rEsr7KLt',
      method: 'PUT',
      body: JSON.stringify({
        id: itemId,
        status: 'deleted',
      }),
    });
  } catch (putError) {
    // Estratégia alternativa: DELETE tradicional
    try {
      await apiFetch(`carrinho_produtos/${itemId}`, {
        versionKey: 'rEsr7KLt',
        method: 'DELETE',
      });
    } catch (deleteError) {
      throw deleteError;
    }
  }
}

// Upsert de item: verifica se já existe e atualiza ou cria novo (evita duplicação real)
export async function upsertCartItem(payload: {
  produto_id?: number;
  servicos_id?: number;
  carrinho_id: number;
  pedido_id: number;
  qtd: number;
  total: number;
  user_id: number;
}): Promise<CartItemRecord> {
  try {
    // Buscar itens existentes deste carrinho
    const existingItems = await getJson<CartItemRecord[]>(
      'rEsr7KLt',
      'carrinho_produtos',
      {
        carrinho_id: payload.carrinho_id,
        pedido_id: payload.pedido_id,
      }
    );

    // Verificar se já existe item com mesmo produto_id ou servicos_id
    let existingItem: CartItemRecord | undefined;
    if (payload.produto_id) {
      existingItem = existingItems.find(
        item => item.produto_id === payload.produto_id
      );
    } else if (payload.servicos_id) {
      existingItem = existingItems.find(
        item => item.servicos_id === payload.servicos_id
      );
    }

    if (existingItem) {
      // Limpar payload: remover undefined e garantir que apenas produto_id OU servicos_id seja enviado
      const cleanPayload: any = {
        carrinho_id: payload.carrinho_id,
        pedido_id: payload.pedido_id,
        qtd: payload.qtd,
        total: payload.total,
        user_id: payload.user_id,
        id: existingItem.id,
      };

      // Adicionar apenas produto_id OU servicos_id (nunca ambos)
      if (payload.produto_id) {
        cleanPayload.produto_id = payload.produto_id;
      } else if (payload.servicos_id) {
        cleanPayload.servicos_id = payload.servicos_id;
      }

      // Estratégia: PUT para atualizar item existente
      try {
        const result = await apiFetch(`carrinho_produtos/${existingItem.id}`, {
          versionKey: 'rEsr7KLt',
          method: 'PUT',
          body: JSON.stringify(cleanPayload),
        });
        return result as CartItemRecord;
      } catch (putError) {
        // Fallback: DELETE + POST
        try {
          await deleteCartItem(existingItem.id);
        } catch (deleteError) {}
      }
    }

    // Criar novo item (ou recriar com valores atualizados)
    const result = await addCartItem(payload);
    return result;
  } catch (error) {
    // Em caso de erro na busca, tentar criar direto
    return await addCartItem(payload);
  }
}
